/* Section renderer (Hero, Body, Image, Pillars, CTA) — ported from the prototype.
   Image fields now use the { url, storagePath, alt } object model; body HTML is
   rendered through the sanitizing RichText component. */
import { Link } from "../lib/router-compat.jsx";
import { SmartImage, Placeholder, BoltDeco } from "../ui/primitives.jsx";
import RichText from "../ui/RichText.jsx";

export default function SectionRenderer({ sections = [], photoForHero }) {
  return (
    <>
      {sections.map((s, idx) => {
        const warm = idx % 2 === 1;
        if (s.type === "hero") return <HeroBlock key={s.id} section={s} narrow={idx > 0} photo={photoForHero} />;
        if (s.type === "body") return <BodyBlock key={s.id} section={s} warm={warm} />;
        if (s.type === "image") return <ImageBlock key={s.id} section={s} warm={warm} />;
        if (s.type === "pillars") return <PillarsBlock key={s.id} section={s} warm={warm} />;
        if (s.type === "cta") return <CTABlock key={s.id} section={s} />;
        return null;
      })}
    </>
  );
}

export function HeroBlock({ section, narrow, photo }) {
  const img = section.image?.url || photo || null;
  return (
    <section className={`hero ${narrow ? "hero--narrow" : ""} container`} style={{ position: "relative" }}>
      <div className="deco-blob" style={{ width: 280, height: 280, background: "var(--accent-pink-soft)", top: -60, right: -80 }} />
      <div className="deco-bolt" style={{ top: 60, right: 40, color: "var(--accent-yellow)", opacity: 0.4 }}>
        <BoltDeco size={64} />
      </div>
      <div className="hero__content" style={{ position: "relative", zIndex: 1 }}>
        {section.eyebrow && <span className="t-eyebrow">{section.eyebrow}</span>}
        <h1 className="t-display" style={{ margin: 0 }}>{section.headline}</h1>
        {section.subheadline && <p className="t-lg muted" style={{ margin: 0, maxWidth: 520 }}>{section.subheadline}</p>}
        {section.ctaLabel && (
          <div className="hero__cta-row">
            <Link to={section.ctaHref || "/"} className="btn btn--primary btn--lg">{section.ctaLabel}</Link>
            {section.secondaryHref && <Link to={section.secondaryHref}>{section.secondaryLabel} →</Link>}
          </div>
        )}
      </div>
      <div className="hero__image" style={{ position: "relative", zIndex: 1 }}>
        {img
          ? <SmartImage src={img} alt={section.image?.alt || section.headline} label={section.headline} />
          : <Placeholder illustration label={section.headline} />}
      </div>
    </section>
  );
}

export function BodyBlock({ section, warm }) {
  return (
    <section className={`section ${warm ? "section--warm" : ""}`}>
      <div className="container">
        <div className="prose">
          {section.heading && <h2 className="t-3xl" style={{ marginTop: 0, marginBottom: 24 }}>{section.heading}</h2>}
          <RichText html={section.bodyHtml || section.body} className="" />
        </div>
      </div>
    </section>
  );
}

export function ImageBlock({ section, warm }) {
  return (
    <section className={`section ${warm ? "section--warm" : ""}`}>
      <div className="container">
        <div style={{ borderRadius: "var(--radius-xl)", overflow: "hidden", aspectRatio: section.aspect === "1:1" ? "1 / 1" : section.aspect === "4:3" ? "4 / 3" : "16 / 9", background: "var(--bg-warm)" }}>
          <SmartImage src={section.image?.url} alt={section.image?.alt || ""} label={section.caption || ""} />
        </div>
        {section.caption && <div className="t-sm muted center" style={{ marginTop: 12, fontStyle: "italic" }}>{section.caption}</div>}
      </div>
    </section>
  );
}

export function PillarsBlock({ section, warm }) {
  const items = section.items || [];
  return (
    <section className={`section ${warm ? "section--warm" : ""}`}>
      <div className="container">
        {section.heading && <h2 className="t-3xl center" style={{ marginTop: 0, marginBottom: 0 }}>{section.heading}</h2>}
        <div className="pillars" style={{ gridTemplateColumns: items.length >= 5 ? "repeat(auto-fit, minmax(200px, 1fr))" : "repeat(2, 1fr)" }}>
          {items.map((it, i) => (
            <div key={i} className="pillar">
              <div className="pillar__num">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="pillar__title" style={{ margin: 0 }}>{it.title}</h3>
              <p className="pillar__body" style={{ margin: 0 }}>{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CTABlock({ section }) {
  return (
    <section className="section section--primary section--lg" style={{ position: "relative", overflow: "hidden" }}>
      <BoltDeco size={300} color="rgba(244, 180, 0, 0.15)" style={{ position: "absolute", top: -40, right: -40 }} />
      <BoltDeco size={200} color="rgba(244, 180, 0, 0.1)" style={{ position: "absolute", bottom: -40, left: 40 }} />
      <div className="container" style={{ position: "relative", textAlign: "center", maxWidth: 720, padding: "0 48px" }}>
        <h2 className="t-3xl" style={{ marginTop: 0, color: "white" }}>{section.heading}</h2>
        {section.body && <p className="t-lg" style={{ color: "rgba(255,255,255,0.85)", marginTop: 16 }}>{section.body}</p>}
        {section.ctaLabel && (
          <div style={{ marginTop: 32 }}>
            <Link to={section.ctaHref || "/donate"} className="btn btn--on-primary btn--lg">{section.ctaLabel}</Link>
          </div>
        )}
      </div>
    </section>
  );
}
